import type { MembershipLevel } from '@ni/shared';
import { DEV_USER } from '../env';
import type {
  DemoCatalog,
  OrgDeletionPreview,
  OrgDeletionResult,
  DemoFixture,
  DeliveryReport,
  ShadowCoverage,
  ShadowUsage,
  ShadowSetting,
  ConfigureShadow,
  CreditChange,
  CreditReceipt,
  CreditView,
  ChargingView,
  ChargingSetting,
  ConfigureCharging,
  ChargeAction,
  CatalogRate,
  PublishRate,
  AdminConnector,
  AdminMember,
  AdminOrg,
  AdminResearchSettings,
  AdminUsage,
  AdminVerticalList,
  ErrorEnvelope,
  Me,
} from './types';

/**
 * The one fetch every call goes through, and the routes on the other side of it
 * (server-design §3.6) plus `GET /me`.
 *
 * **Dev auth** (local-dev.md §2.4) is the default and needs no provider:
 * `x-dev-user` names the caller and `x-dev-admin: 1` is the Auth0 RBAC
 * substitute for the `admin` permission — the same permission that gates every
 * `/admin/*` route and bypasses `OrgAccessGuard`. The default caller is the
 * seeded operator account, which holds the permission and **no membership row**;
 * that is the shape of a real operator, not an accident of seeding.
 *
 * **Auth0 mode** swaps those two headers for a bearer token by installing its
 * own `authorize` (see `installAuth`, called from `src/auth.tsx`). That is the
 * whole seam: `api` stays a flat object of module functions, every call site
 * stays `api.listOrgs()`, and nothing in the page knows which mode it is under.
 */

/** Puts whatever proves who is calling onto the outgoing headers. */
export type Authorize = (headers: Headers) => Promise<void> | void;

const devAuthorize: Authorize = (headers) => {
  headers.set('x-dev-user', DEV_USER);
  headers.set('x-dev-admin', '1');
};

let authorize: Authorize = devAuthorize;
let onUnauthorized: (() => void) | null = null;
let onAccepted: (() => void) | null = null;

/**
 * Auth0 mode installs its bearer path here. Called during the auth provider's
 * render rather than from an effect on purpose: the page's own load effects run
 * *before* a parent's effect would, so the first `GET /me` has to find the live
 * `authorize` already in place. The assignment is idempotent module state, which
 * is what makes that safe.
 *
 * `onAccepted` is the other half of `onUnauthorized`, and it exists because the
 * provider's one-shot re-login guard has to be retired by evidence: any answer
 * that is not a 401 proves the session reaches the server. Keeping both hooks
 * here rather than importing the provider's storage helpers is what keeps this
 * module free of an import cycle with `src/auth.tsx`.
 */
export function installAuth(hooks: {
  authorize: Authorize;
  onUnauthorized: () => void;
  onAccepted: () => void;
}): void {
  authorize = hooks.authorize;
  onUnauthorized = hooks.onUnauthorized;
  onAccepted = hooks.onAccepted;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  headers.set('accept', 'application/json');
  await authorize(headers);
  if (init?.body !== undefined) headers.set('content-type', 'application/json');

  const response = await fetch(path, { ...init, headers });
  // ui-design §6.3: 401 → sign in again. Dev mode installs no handler, so a
  // 401 there stays what it always was — an error line.
  if (response.status === 401) onUnauthorized?.();
  else onAccepted?.();
  if (!response.ok) throw await toApiError(response);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

async function toApiError(response: Response): Promise<ApiError> {
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    body = null;
  }
  const envelope = body as ErrorEnvelope | null;
  if (envelope?.error?.code) {
    return new ApiError(response.status, envelope.error.code, envelope.error.message);
  }
  return new ApiError(
    response.status,
    `HTTP_${response.status}`,
    response.statusText || 'Request failed.',
  );
}

/**
 * The few conflict-registry codes an admin write can earn (server-design §7.8),
 * each reading exactly as it does on the dashboard. Hand-written like the rest
 * of this app's wire types: one small table is cheaper than taking a dependency
 * on @ni/shared for three lines of copy.
 */
const CODE_LINES: Record<string, string> = {
  VALIDATION_FAILED: 'That was not accepted. Nothing was saved.',
  OWNERSHIP_FENCE: 'Someone else changed this first.',
  IDEMPOTENCY_KEY_REUSED: 'Someone else changed this first.',
};

export const errorLine = (error: unknown): string => {
  if (!(error instanceof ApiError)) return 'The server did not answer.';
  if (error.status === 403) return 'This account does not hold the admin permission.';
  return CODE_LINES[error.code] ?? error.message;
};

export const api = {
  rebuildDemo: (
    scenario: DemoFixture['scenario'],
    body: {
      manifest_sha256: string;
      environment: DemoCatalog['environment'];
      request_id: string;
      targets: { org_id: string; installation_id: string; manifest_sha256: string }[];
    },
  ) =>
    request<DemoFixture>(`/admin/demo-fixtures/${scenario}/rebuild`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  demoFixtures: () => request<DemoCatalog>('/admin/demo-fixtures'),
  installDemo: (
    scenario: DemoFixture['scenario'],
    body: { manifest_sha256: string; environment: DemoCatalog['environment'] },
  ) =>
    request<DemoFixture>(`/admin/demo-fixtures/${scenario}/install`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  deliveryReport: (orgId: string) =>
    request<DeliveryReport>(`/admin/orgs/${orgId}/credits/deliveries`),
  shadowCoverage: (orgId: string, period?: { start: string; end: string }) =>
    request<ShadowCoverage>(
      `/admin/orgs/${orgId}/credits/shadow/coverage${period ? `?${new URLSearchParams(period)}` : ''}`,
    ),
  shadowUsage: (orgId: string, before?: string) =>
    request<ShadowUsage>(
      `/admin/orgs/${orgId}/credits/shadow${before ? `?before=${encodeURIComponent(before)}` : ''}`,
    ),
  configureShadow: (orgId: string, body: ConfigureShadow) =>
    request<ShadowSetting>(`/admin/orgs/${orgId}/credits/shadow/settings`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  charging: (orgId: string) => request<ChargingView>(`/admin/orgs/${orgId}/credits/charging`),
  configureCharging: (orgId: string, body: ConfigureCharging) =>
    request<ChargingSetting>(`/admin/orgs/${orgId}/credits/charging/settings`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  reconcileCharge: (orgId: string, chargeId: string) =>
    request<ChargeAction>(`/admin/orgs/${orgId}/credits/charging/charges/${chargeId}/reconcile`, {
      method: 'POST',
    }),
  releaseCharge: (orgId: string, chargeId: string, reason: string) =>
    request<ChargeAction>(`/admin/orgs/${orgId}/credits/charging/charges/${chargeId}/release`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
  publishRate: (body: PublishRate) =>
    request<CatalogRate>('/admin/credits/catalog/rates', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  dispatchCharge: (orgId: string, chargeId: string) =>
    request<{ enqueued: boolean }>(
      `/admin/orgs/${orgId}/credits/charging/charges/${chargeId}/dispatch`,
      { method: 'POST' },
    ),
  credits: (orgId: string, before?: number) =>
    request<CreditView>(`/admin/orgs/${orgId}/credits${before ? `?before=${before}` : ''}`),
  changeCredits: (orgId: string, change: CreditChange) => {
    const { kind, ...body } = change;
    const path = { grant: 'grants', revoke: 'revocations', refund: 'refunds' }[kind];
    return request<CreditReceipt>(`/admin/orgs/${orgId}/credits/${path}`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },
  me: () => request<Me>('/me'),

  listOrgs: () => request<{ orgs: AdminOrg[] }>('/admin/orgs'),
  deletionPreview: (orgId: string) => request<OrgDeletionPreview>(`/admin/orgs/${orgId}/deletion`),
  deleteOrg: (orgId: string, confirmName: string) =>
    request<OrgDeletionResult>(`/admin/orgs/${orgId}/deletion`, {
      method: 'POST',
      body: JSON.stringify({ confirm_name: confirmName }),
    }),

  /** The org types this build carries, and which one a body naming none gets. */
  listVerticals: () => request<AdminVerticalList>('/admin/verticals'),

  createOrg: (body: { slug: string; name: string; vertical?: string }) =>
    request<{ org: AdminOrg }>('/admin/orgs', { method: 'POST', body: JSON.stringify(body) }),

  listMembers: (orgId: string) =>
    request<{ members: AdminMember[] }>(`/admin/orgs/${orgId}/members`),

  /**
   * Invite = pre-provision: the user row and the membership, nothing else.
   *
   * `level` is required by the route and has no default there — an invite that
   * granted the run of an organization because a field was omitted is the
   * failure the column exists to prevent.
   */
  addMember: (orgId: string, body: { email: string; name?: string; level: MembershipLevel }) =>
    request<{ user: { id: string; email: string; name: string | null } }>(
      `/admin/orgs/${orgId}/members`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  /** Change what an existing member may do. Re-inviting them does not do this. */
  setMemberLevel: (orgId: string, userId: string, level: MembershipLevel) =>
    request<{ member: AdminMember }>(`/admin/orgs/${orgId}/members/${userId}/level`, {
      method: 'PATCH',
      body: JSON.stringify({ level }),
    }),

  /** Revoke access. Membership rows only — never substrate data. */
  removeMember: (orgId: string, userId: string) =>
    request<{ removed: boolean }>(`/admin/orgs/${orgId}/members/${userId}`, { method: 'DELETE' }),

  /** Research spend for the month. The office has no route to this (2026-08-27). */
  usage: (orgId: string, month?: string) =>
    request<AdminUsage>(
      `/admin/orgs/${orgId}/usage${month ? `?month=${encodeURIComponent(month)}` : ''}`,
    ),

  /**
   * The connector's three strings, including the client secret.
   *
   * **Called on a click and never on a mount.** The secret is display-only —
   * nothing in the server authenticates with it — but a value nobody asked for
   * should not cross the wire or sit in this app's memory, so the panel fetches
   * this route when somebody presses Reveal and forgets it when they close the
   * panel. There is no org in the path: one Auth0 client serves every
   * organization, and the token is what names the caller.
   */
  connector: () => request<AdminConnector>('/admin/mcp/connector'),

  /**
   * The org's research settings — the depth per status and the spend limit per
   * run. Both are the operator's (2026-09-04) and this is the only door that
   * writes them.
   *
   * The bag is sent **whole and replaces what is stored**: a key left out of it
   * is a choice taken back, which is exactly what "Use the default" means, and
   * `{}` takes them all back. `budget_usd: null` is the emptied field's way of
   * saying "no limit" — a control cannot omit itself.
   *
   * It answers with the org row, settings included, so the caller replaces what
   * it holds rather than re-reading the list.
   */
  updateOrgSettings: (orgId: string, research: AdminResearchSettings) =>
    request<{ org: AdminOrg }>(`/admin/orgs/${orgId}/settings`, {
      method: 'PATCH',
      body: JSON.stringify({ research }),
    }),

  /**
   * **The organization's monthly spend limit** — the same door, a different
   * panel (2026-09-09).
   *
   * Its own method rather than a second argument to the one above, because the
   * two panels are two writers: naming a key on this door **writes** it and not
   * naming it **leaves it alone**, so the limit's control posts the limit alone
   * and the research controls post the bag alone, and neither can clear the
   * other by rendering it and not showing it.
   *
   * `null` is the emptied field saying **no limit** — a control cannot omit
   * itself, so it says it out loud, and the server stores it by storing no key.
   *
   * It answers with the org row, settings included, like its neighbour.
   */
  updateMonthlySpendLimit: (orgId: string, usd: number | null) =>
    request<{ org: AdminOrg }>(`/admin/orgs/${orgId}/settings`, {
      method: 'PATCH',
      body: JSON.stringify({ monthly_spend_ceiling_usd: usd }),
    }),
};
